<script>
import { mapGetters, mapActions } from 'vuex'
import {
  processCardNumber,
  processExpiryDate,
  processCVV,
} from '@tamara/credit-card-input-processing'
import BaseButton from '@/components/lavender/v2/BaseButton.vue'

export default {
  name: 'PaymentForm',
  components: {
    BaseButton,
  },
  props: {
    // Custom cancel handler
    onCancel: {
      type: Function,
      required: true,
    },
    // Custom pay handler
    onPay: {
      type: Function,
      required: true,
    },
    // Pay button text
    payButtonText: {
      type: String,
      required: true,
    },
    // Whether to keep payment data on destroy
    keepPaymentData: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isSubmitting: false,
      paymentMethod: null,
      isKeepPaymentData: false,
      card: {
        number: '',
        scheme: '',
        valid: null, // `null` for untouched, later true/false
        touched: false, // Track if user has interacted with this field
      },
      expiry: {
        month: '',
        year: '',
        valid: null, // `null` for untouched, later true/false
        touched: false, // Track if user has interacted with this field
      },
      cvv: {
        value: '',
        valid: null, // `null` for untouched, later true/false
        touched: false, // Track if user has interacted with this field
      },
    }
  },
  computed: {
    ...mapGetters('ads', ['paymentData']),
    paymentMethodIcon() {
      if (!this.paymentMethod) {
        return null
      }

      return `payment-method-${this.paymentMethod}`
    },
    cardSchemeIcon() {
      if (!this.card.scheme) {
        return null
      }

      return {
        visa: 'visa',
        master: 'mastercard',
        amex: 'american-express',
        mada: 'mada',
        maestro: 'maestro',
      }[this.card.scheme]
    },
    supportedMethodIcons() {
      // If user has typed and we detected a card scheme, show that specific icon
      if (this.card.touched && this.card.scheme) {
        return [`payment-method-${this.cardSchemeIcon}`]
      }

      // Otherwise show all supported payment method icons
      return this.supportedPaymentMethods.map(
        (method) => `payment-method-${method}`
      )
    },
    supportedPaymentMethods() {
      if (!this.paymentData?.billingAddress) {
        return []
      }

      const { countryCode } = this.paymentData.billingAddress
      const methodList = ['visa', 'mastercard', 'american-express']

      if (countryCode === 'SA') {
        methodList.push('mada')
      }

      return methodList
    },
    isCardSupported() {
      if (!this.paymentMethod) {
        return true
      }

      return this.supportedPaymentMethods.includes(this.paymentMethod)
    },
    isFormValid() {
      return this.card.valid && this.expiry.valid && this.cvv.valid
    },
  },
  destroyed() {
    if (!this.isKeepPaymentData) {
      this.setPaymentData(null)
    }
  },
  methods: {
    ...mapActions('ads', ['setPaymentData']),
    onCardNumberInput(event) {
      const { number, scheme, mask, valid } = processCardNumber(
        event.target.value
      )

      event.target.value = mask

      this.card = {
        number,
        scheme,
        valid,
        touched: true,
      }
    },
    onExpiryDateInput(event) {
      const { month, year, mask, valid } = processExpiryDate(event.target.value)

      event.target.value = mask

      this.expiry = {
        month,
        year,
        valid,
        touched: true,
      }
    },
    onCVVInput(event) {
      const { value, valid } = processCVV(event.target.value)

      event.target.value = value

      this.cvv = {
        value,
        valid,
        touched: true,
      }
    },
    handleCancel() {
      this.onCancel()
    },
    async handlePay() {
      // Validate form before proceeding
      if (!this.isFormValid) {
        return
      }

      try {
        this.isSubmitting = true

        await this.onPay({
          cardNumber: this.card.number,
          expiryMonth: Number(this.expiry.month),
          expiryYear: Number(this.expiry.year),
          cvv: this.cvv.value,
          paymentData: this.paymentData,
        })
      } catch (error) {
        console.error(error)
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<template>
  <form id="payment-form">
    <div class="payment-form-header">
      <h1>{{ $t('ads_top_up_payment_method') }}</h1>
      <p>
        {{ $t('ads_top_up_secure_msg') }}
      </p>
    </div>
    <div class="flex flex-col gap-4">
      <div class="w-full">
        <label>
          <span>{{ $t('ads_top_up_card_number') }}</span>
          <div class="flex items-center gap-2 relative">
            <input
              type="text"
              :class="{
                'payment-input': true,
                error: (card.touched && !card.valid) || !isCardSupported,
              }"
              :placeholder="$t('ads_top_up_card_number')"
              @input="onCardNumberInput($event)"
            />
            <svg-icon
              v-if="paymentMethodIcon"
              class="payment-method-icon supported-methods"
              :icon-class="paymentMethodIcon"
            />
            <div v-else class="supported-methods">
              <svg-icon
                v-for="methodIcon in supportedMethodIcons"
                :key="methodIcon"
                class="payment-method-icon"
                :icon-class="methodIcon"
              />
            </div>
          </div>
          <span v-if="!isCardSupported" class="error-msg">
            {{ $t('ads_top_up_unsupported_method') }}
          </span>
          <span v-else-if="card.touched && !card.valid" class="error-msg">
            {{ $t('ads_top_up_card_error_msg') }}
          </span>
        </label>
      </div>
      <div class="flex gap-4 w-full">
        <label class="flex-1">
          <span>{{ $t('ads_top_up_valid_thru') }}</span>
          <input
            type="text"
            :class="{
              'payment-input': true,
              error: expiry.touched && !expiry.valid,
            }"
            placeholder="MM/YY"
            pattern="^\d{2}\/\d{2}$"
            @input="onExpiryDateInput($event)"
          />
          <span v-if="expiry.touched && !expiry.valid" class="error-msg">
            {{ $t('ads_top_up_expiry_date_error_msg') }}
          </span>
        </label>
        <label class="flex-1">
          <span>{{ $t('ads_top_up_cvv') }}</span>
          <input
            type="text"
            :class="{ 'payment-input': true, error: cvv.touched && !cvv.valid }"
            placeholder="CVV"
            pattern="\d{3,4}"
            @input="onCVVInput($event)"
          />
          <span v-if="cvv.touched && !cvv.valid" class="error-msg">
            {{ $t('ads_top_up_cvv_error_msg') }}
          </span>
        </label>
      </div>
    </div>

    <div class="flex gap-4 col-span-2">
      <BaseButton
        :disabled="isSubmitting"
        type="tertiary"
        class="flex-1"
        @click="handleCancel"
      >
        {{ $t('ads_top_up_cancel') }}
      </BaseButton>
      <BaseButton
        :loading="isSubmitting"
        :disabled="!isFormValid"
        type="primary"
        class="flex-1 whitespace-no-wrap"
        @click="handlePay"
      >
        {{ payButtonText }}
      </BaseButton>
    </div>
  </form>
</template>

<style lang="scss" scoped>
form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

label {
  position: relative;
  display: flex;
  flex-direction: column;
  padding-block-end: 24px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--content-mid);

  > span {
    display: block;
    margin-block-end: 8px;
    text-align: start;
  }
}

.payment-form-header {
  width: 100%;
  text-align: start;

  h1 {
    margin-block-end: 16px !important;
  }

  p {
    margin-block-end: 16px !important;
  }
}

.payment-input {
  position: relative;
  width: 100%;
  height: 44px;
  padding: 8px 16px;
  border: 1px solid var(--border-mid);
  border-radius: 8px;

  &:focus-visible {
    outline: none;
  }

  &.error {
    border: 1px solid var(--error-intense);
  }
}

.error-msg {
  position: absolute;
  top: calc(100% - 24px);
  margin-block-start: 8px;
  font-size: 12px;
  line-height: 1;
  color: var(--error-text);
}

.card-number-input {
  grid-area: number;
}

.supported-methods {
  position: absolute;
  top: 11px;
  right: 8px;
  display: flex;
  gap: 4px;

  [dir='rtl'] & {
    right: unset;
    left: 8px;
  }
}

.payment-method-icon {
  width: 36px !important;
  height: 20px !important;
}
</style>
